<script setup>
import { computed, ref } from 'vue'
import { humanReadableDuration, humanReadableTimestamp } from '@/composables/datetime.js'
import { canTriggerBarnDocumentation } from '@/composables/permissionHelper.js'
import TriggerBarnDocumentation from '@/components/incidentTriggers/TriggerBarnDocumentation.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const copyToBarnDocumentationDialog = ref(false)

const statusChipText = computed(() => {
  return t('incidents.timeSeries.status.' + props.incident.state)
})

const hasAbnormalState = computed(() => {
  return props.incident.state === 'wasAbnormal' || props.incident.state === 'isAbnormal'
})

const statusIcon = computed(() => {
  if (props.incident.state === 'closed') {
    return 'mdi-lock'
  }
  if (props.incident.state === 'open') {
    return 'mdi-lock-open-variant'
  }
  if (props.incident.state === 'wasAbnormal') {
    return 'mdi-alert'
  }
  if (props.incident.state === 'isAbnormal') {
    return 'mdi-alert'
  }
  return 'mdi-information'
})

const duration = computed(() => {
  return humanReadableDuration(
    props.incident.firstEventTimestamp,
    props.incident.state === 'closed' ? props.incident.lastEventTimestamp : Date.now(),
    ['d', 'h', 'm'],
    true
  )
})

const inlineIncidentTag = computed(() => {
  return `<inline-incident incident-id="${props.incident.incidentId}"></inline-incident>`
})

const scoreColor = computed(() => {
  if (props.incident.messagePayload.score < props.incident.messagePayload.options?.threshold) {
    return 'success'
  }
  return 'error'
})

const isAllowedToTriggerBarnDocumentation = computed(() => {
  return canTriggerBarnDocumentation()
})
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-start flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-chart-line"
            start
          />
          {{ $t('incidents.timeSeries.title') }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            :variant="props.incident.state !== 'wasAbnormal' ? 'flat' : 'tonal'"
            :color="hasAbnormalState ? 'error' : 'default'"
          >
            {{ statusChipText }}
          </v-chip>
          <v-chip
            class="px-4"
            prepend-icon="mdi-label"
            size="small"
            variant="tonal"
            color="primary"
          >
            {{ incident.messagePayload.detectionLabel }}
          </v-chip>
        </div>
      </div>

      <div class="d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>
    <v-card-text class="py-2">
      <v-list-item
        :title="$t('incidents.timeSeries.detection_name')"
        prepend-icon="mdi-text-box-outline"
        :subtitle="incident.identifier.detectionName"
      />
      <v-list-item
        v-if="incident.messagePayload.options?.evaluation_variable"
        :title="$t('incidents.timeSeries.evaluation_variable')"
        prepend-icon="mdi-variable"
        :subtitle="incident.messagePayload.options.evaluation_variable"
      />
      <v-list-item
        :title="$t('incidents.timeSeries.start_timestamp')"
        :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        prepend-icon="mdi-clock-start"
      />
      <v-list-item
        v-if="incident.state === 'closed'"
        :title="$t('incidents.timeSeries.end_timestamp')"
        :subtitle="humanReadableTimestamp(incident.lastEventTimestamp)"
        prepend-icon="mdi-clock-end"
      />
      <v-list-item
        :title="$t('incidents.timeSeries.duration')"
        :subtitle="duration"
        prepend-icon="mdi-timer"
      />
      <v-list-item
        prepend-icon="mdi-counter"
        :title="$t('incidents.timeSeries.current_score')"
        :subtitle="incident.messagePayload.score.toFixed(5)"
        :base-color="scoreColor"
      />
      <v-list-item
        v-if="incident.messagePayload.options?.threshold"
        :title="$t('incidents.timeSeries.threshold')"
        prepend-icon="mdi-numeric"
        :subtitle="incident.messagePayload.options.threshold"
      />
      <v-list-item
        v-if="incident.messagePayload.options?.abnormalThreshold"
        :title="$t('incidents.timeSeries.threshold_abnormal')"
        prepend-icon="mdi-alert"
        :subtitle="incident.messagePayload.options.abnormalThreshold"
      />

      <v-btn
        v-if="isAllowedToTriggerBarnDocumentation"
        variant="flat"
        color="primary"
        class="ma-2"
      >
        {{ $t('incidents.timeSeries.copy_to_barn_documentation') }}

        <v-dialog
          v-model="copyToBarnDocumentationDialog"
          max-width="800px"
          activator="parent"
        >
          <TriggerBarnDocumentation
            :pre-filled-first-timestamp="incident.firstEventTimestamp"
            :pre-filled-second-timestamp="incident.lastEventTimestamp"
            :pre-filled-description="$t('incidents.timeSeries.copied-from', { incident: inlineIncidentTag })"
          />
        </v-dialog>
      </v-btn>
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot name="genericActions" />
    </v-card-actions>
  </v-layout-card>
</template>

<style scoped></style>
